import { apiClient } from '@consentropy/shared';
import { API_PATHS } from '@consentropy/shared';
import type { ApiListResponse, ApiItemResponse } from '@consentropy/shared';
import type { Member, CreateMemberInput, UpdateMemberInput } from '@consentropy/shared';

const DELETED_MEMBER_KEY = 'consentropy.deletedMember';
const DELETED_MEMBER_TTL_MS = 10 * 60 * 1000;
const CREATED_MEMBER_KEY = 'consentropy.createdMember';
const CREATED_MEMBER_TTL_MS = 10 * 60 * 1000;
const UPDATED_MEMBER_KEY = 'consentropy.updatedMember';
const UPDATED_MEMBER_TTL_MS = 10 * 60 * 1000;

type MemberDeletionTombstone = {
  projectId: string;
  deletedAt: number;
};

type PersistedCreatedMember = {
  member: Member;
  createdAt: number;
};

type PersistedUpdatedMember = {
  member: Member;
  updatedAt: number;
};

function getStorage(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function readWithTtl<T extends object>(
  key: string,
  timeField: keyof T,
  ttlMs: number,
): Record<string, T> {
  const storage = getStorage();
  if (!storage) return {};

  const raw = storage.getItem(key);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Record<string, T>;
    const now = Date.now();
    const active = Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => {
        const timestamp = value[timeField];
        return typeof timestamp === 'number' && now - timestamp < ttlMs;
      }),
    ) as Record<string, T>;

    if (Object.keys(active).length !== Object.keys(parsed).length) {
      writeStorageRecord(key, active);
    }

    return active;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures for this fallback layer.
    }
    return {};
  }
}

function writeStorageRecord<T extends object>(key: string, record: Record<string, T>): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (Object.keys(record).length === 0) {
      storage.removeItem(key);
      return;
    }

    storage.setItem(key, JSON.stringify(record));
  } catch {
    // This fallback should never break the main data flow if storage is unavailable.
  }
}

function readDeletedMember(): Record<string, MemberDeletionTombstone> {
  return readWithTtl(DELETED_MEMBER_KEY, 'deletedAt', DELETED_MEMBER_TTL_MS);
}

function writeDeletedMember(
  tombstones: Record<string, MemberDeletionTombstone>,
): void {
  writeStorageRecord(DELETED_MEMBER_KEY, tombstones);
}

function readCreatedMember(): Record<string, PersistedCreatedMember> {
  return readWithTtl(CREATED_MEMBER_KEY, 'createdAt', CREATED_MEMBER_TTL_MS);
}

function writeCreatedMember(
  memberRecord: Record<string, PersistedCreatedMember>,
): void {
  writeStorageRecord(CREATED_MEMBER_KEY, memberRecord);
}

function readUpdatedMember(): Record<string, PersistedUpdatedMember> {
  return readWithTtl(UPDATED_MEMBER_KEY, 'updatedAt', UPDATED_MEMBER_TTL_MS);
}

function writeUpdatedMember(
  memberRecord: Record<string, PersistedUpdatedMember>,
): void {
  writeStorageRecord(UPDATED_MEMBER_KEY, memberRecord);
}

export function saveCreatedMember(member: Member): void {
  const createdMemberRecord = readCreatedMember();
  createdMemberRecord[member.id] = { member, createdAt: Date.now() };
  writeCreatedMember(createdMemberRecord);
}

export function saveUpdatedMember(member: Member): void {
  const updatedMemberRecord = readUpdatedMember();
  updatedMemberRecord[member.id] = { member, updatedAt: Date.now() };
  writeUpdatedMember(updatedMemberRecord);
}

export function saveMemberDeletionTombstone(id: string, projectId: string): void {
  const tombstones = readDeletedMember();
  tombstones[id] = { projectId, deletedAt: Date.now() };
  writeDeletedMember(tombstones);

  const createdMemberRecord = readCreatedMember();
  if (createdMemberRecord[id]) {
    delete createdMemberRecord[id];
    writeCreatedMember(createdMemberRecord);
  }

  const updatedMemberRecord = readUpdatedMember();
  if (updatedMemberRecord[id]) {
    delete updatedMemberRecord[id];
    writeUpdatedMember(updatedMemberRecord);
  }
}

export function clearMemberDeletionTombstone(id: string): void {
  const tombstones = readDeletedMember();
  if (!tombstones[id]) return;

  delete tombstones[id];
  writeDeletedMember(tombstones);
}

function filterDeletedMember(memberList: Member[], projectId: string): Member[] {
  const tombstones = readDeletedMember();
  let changed = false;

  const filtered = memberList.filter((member) => {
    const tombstone = tombstones[member.id];
    const shouldHide = tombstone?.projectId === projectId;

    if (!shouldHide && tombstone) {
      delete tombstones[member.id];
      changed = true;
    }

    return !shouldHide;
  });

  if (changed) {
    writeDeletedMember(tombstones);
  }

  return filtered;
}

function mergeCreatedMember(memberList: Member[], projectId: string): Member[] {
  const createdMemberRecord = readCreatedMember();
  const merged = new Map(memberList.map((member) => [member.id, member]));

  for (const [memberId, value] of Object.entries(createdMemberRecord)) {
    const createdMember = value.member;
    if (createdMember.projectId !== projectId) continue;

    if (merged.has(memberId)) {
      // Keep the fallback until its TTL expires in case a later list response
      // comes from an API instance with an older cached snapshot.
      continue;
    }

    merged.set(memberId, createdMember);
  }

  return Array.from(merged.values());
}

function mergeUpdatedMember(memberList: Member[], projectId: string): Member[] {
  const updatedMemberRecord = readUpdatedMember();
  let changed = false;
  const merged = new Map(memberList.map((member) => [member.id, member]));

  for (const [memberId, value] of Object.entries(updatedMemberRecord)) {
    const updatedMember = value.member;
    if (updatedMember.projectId !== projectId) continue;

    const fetchedMember = merged.get(memberId);
    if (fetchedMember && JSON.stringify(fetchedMember) === JSON.stringify(updatedMember)) {
      delete updatedMemberRecord[memberId];
      changed = true;
      continue;
    }

    merged.set(memberId, updatedMember);
  }

  if (changed) {
    writeUpdatedMember(updatedMemberRecord);
  }

  return Array.from(merged.values());
}

export async function getMemberByProject(projectId: string): Promise<Member[]> {
  const items: Member[] = [];
  let cursor: string | Record<string, unknown> | null | undefined;
  do {
    const params = new URLSearchParams({ projectId });
    if (cursor) params.set('lastEvaluatedKey', typeof cursor === 'string' ? cursor : JSON.stringify(cursor));
    const page: Omit<ApiListResponse<Member>, 'lastEvaluatedKey'> & { lastEvaluatedKey?: typeof cursor } = await apiClient.get(`${API_PATHS.member}?${params}`);
    items.push(...page.items);
    cursor = page.lastEvaluatedKey;
  } while (cursor);
  return mergeUpdatedMember(
    mergeCreatedMember(
      filterDeletedMember(
        items.filter((member) => member.projectId === projectId),
        projectId,
      ),
      projectId,
    ),
    projectId,
  );
}

export async function createMember(input: CreateMemberInput): Promise<Member> {
  const response = await apiClient.post<ApiItemResponse<Member>>(API_PATHS.member, input);
  return response.item;
}

export async function updateMember(id: string, input: UpdateMemberInput): Promise<Member> {
  const response = await apiClient.patch<ApiItemResponse<Member>>(
    `${API_PATHS.member}${id}`,
    input,
  );
  return response.item;
}

export async function deleteMember(id: string): Promise<void> {
  await apiClient.delete(`${API_PATHS.member}${id}`);
}
